/**
 * iter 59 — Active Sessions panel.
 *
 * Visible to every user (shows their own sessions). If the user has role
 * admin or super_admin, a "All org sessions" toggle lets them audit + revoke
 * every device signed in to their organisation.
 *
 * Revoking a session sets `revoked_at` on the row; the auth middleware
 * rejects any subsequent request carrying that session_id with 401, which
 * forces the device back to the login screen on its next API call.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { useBrand } from "./AppShell";
import { ArrowsClockwise, X, DesktopTower, DeviceMobile, Browser } from "@phosphor-icons/react";

function fmtDate(s) {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function relativeFromNow(s) {
  if (!s) return "—";
  const d = new Date(s).getTime();
  if (Number.isNaN(d)) return "—";
  const secs = Math.max(0, Math.floor((Date.now() - d) / 1000));
  if (secs < 60)       return `${secs}s ago`;
  if (secs < 3600)     return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400)    return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 86400*7)  return `${Math.floor(secs / 86400)}d ago`;
  return new Date(s).toLocaleDateString();
}

function DeviceIcon({ ua }) {
  const isMobile = /(iPhone|iPad|Android)/i.test(ua || "");
  if (isMobile) return <DeviceMobile size={14} />;
  return <DesktopTower size={14} />;
}

function locationOf(row) {
  const parts = [];
  const head = [row.geo_city, row.geo_region].filter(Boolean).join(", ");
  if (head) parts.push(head);
  if (row.geo_country) parts.push(row.geo_country);
  return parts.join(" · ");
}

export default function SessionsSection({ canViewOrg = false }) {
  const { platformName } = useBrand();
  const [scope, setScope] = useState("mine");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/sessions?scope=${scope}`);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const revoke = async (row) => {
    if (row.is_current) {
      toast.error("You can't revoke your current session — sign out instead.");
      return;
    }
    const who = row.user_email && row.user_id !== row.user_email
      ? `${row.user_name || row.user_email}'s session`
      : "this session";
    if (!window.confirm(`Revoke ${who} on ${row.browser || "Unknown"} / ${row.os || "Unknown"}? The device will be signed out on its next request.`)) {
      return;
    }
    setBusy(row.id);
    try {
      await api.delete(`/sessions/${row.id}`);
      toast.success("Session revoked.");
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(null);
    }
  };

  const revokeOthers = async (targetUserId) => {
    const isSelf = !targetUserId;
    const count = isSelf
      ? items.filter((i) => !i.revoked_at && !i.is_current && i.user_id).length
      : items.filter((i) => !i.revoked_at && i.user_id === targetUserId).length;
    if (count === 0) {
      toast.info(isSelf ? "No other sessions to revoke." : "No active sessions for that user.");
      return;
    }
    const msg = isSelf
      ? `Revoke all ${count} of your OTHER sessions? Every other device will be signed out on its next API call. Your current session stays active.`
      : `Revoke ALL ${count} sessions for this user? Every device will be signed out on its next API call.`;
    if (!window.confirm(msg)) return;
    setBusy("bulk");
    try {
      const url = isSelf ? "/sessions/revoke-others"
                         : `/sessions/revoke-others?user_id=${encodeURIComponent(targetUserId)}`;
      const r = await api.post(url);
      toast.success(`Revoked ${r.data?.revoked ?? count} session(s).`);
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(null);
    }
  };

  const active = items.filter((i) => !i.revoked_at);
  const revoked = items.filter((i) => i.revoked_at);

  return (
    <div className="surface p-5 space-y-5" data-testid="sessions-section">
      <div>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="label-xs mb-1">ACTIVE SESSIONS</div>
            <p className="text-sm text-muted-foreground">
              Every device signed in to {platformName}. Revoke any session you don&apos;t recognise — the device is kicked out on its next API call.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              className="btn btn-secondary text-xs"
              disabled={loading}
              data-testid="sessions-refresh"
              title="Refresh"
            >
              <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} /> REFRESH
            </button>
            <button
              type="button"
              onClick={() => revokeOthers()}
              className="btn btn-secondary text-xs"
              disabled={loading || busy === "bulk"}
              data-testid="sessions-revoke-others"
              title="Revoke every other device signed in to your account"
            >
              <X size={12} /> {busy === "bulk" ? "REVOKING…" : "REVOKE ALL OTHERS"}
            </button>
          </div>
        </div>

        {canViewOrg && (
          <div className="mt-3 inline-flex rounded-md border border-border overflow-hidden text-xs" data-testid="sessions-scope">
            <button
              type="button"
              className={`px-3 py-1.5 ${scope === "mine" ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted/50"}`}
              onClick={() => setScope("mine")}
              data-testid="sessions-scope-mine"
            >
              MY SESSIONS
            </button>
            <button
              type="button"
              className={`px-3 py-1.5 border-l border-border ${scope === "org" ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted/50"}`}
              onClick={() => setScope("org")}
              data-testid="sessions-scope-org"
            >
              ALL ORG SESSIONS
            </button>
          </div>
        )}
      </div>

      {loading && (
        <div className="text-xs text-muted-foreground" data-testid="sessions-loading">Loading sessions…</div>
      )}

      {!loading && active.length === 0 && revoked.length === 0 && (
        <div className="text-xs text-muted-foreground italic" data-testid="sessions-empty">
          No sessions yet. They&apos;ll appear here after you make your next API call.
        </div>
      )}

      {(active.length > 0 || revoked.length > 0) && (
        <div className="overflow-x-auto rounded-md border border-border" data-testid="sessions-table-wrap">
          <table className="w-full text-sm" data-testid="sessions-table">
            <thead className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr>
                {scope === "org" && <th className="px-3 py-2 text-left">User</th>}
                <th className="px-3 py-2 text-left">Device</th>
                <th className="px-3 py-2 text-left">Location</th>
                <th className="px-3 py-2 text-left">IP address</th>
                <th className="px-3 py-2 text-left">Last seen</th>
                <th className="px-3 py-2 text-left">Created</th>
                <th className="px-3 py-2 text-right w-20">Action</th>
              </tr>
            </thead>
            <tbody>
              {active.map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`session-row-${r.id}`}>
                  {scope === "org" && (
                    <td className="px-3 py-2 truncate">
                      <div className="font-medium">{r.user_name || "—"}</div>
                      <div className="text-[10px] text-muted-foreground">{r.user_email}</div>
                    </td>
                  )}
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5 font-medium">
                      <DeviceIcon ua={r.user_agent} />
                      {r.browser} <span className="text-muted-foreground">on</span> {r.os}
                      {r.is_current && (
                        <span className="ml-1.5 px-1.5 py-0.5 text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-800 rounded">
                          this device
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground truncate max-w-md" title={r.user_agent}>
                      {r.user_agent || "—"}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground" data-testid={`session-location-${r.id}`}>
                    {locationOf(r) || <span className="italic text-[11px]">unknown</span>}
                  </td>
                  <td className="px-3 py-2 mono text-xs">{r.ip_address || "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground" title={fmtDate(r.last_seen_at)}>{relativeFromNow(r.last_seen_at)}</td>
                  <td className="px-3 py-2 text-muted-foreground">{fmtDate(r.created_at)}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      className="btn btn-secondary text-xs"
                      onClick={() => revoke(r)}
                      disabled={busy === r.id || r.is_current}
                      data-testid={`session-revoke-${r.id}`}
                      title={r.is_current ? "Sign out from the menu to drop this session" : "Revoke"}
                    >
                      <X size={12} /> {busy === r.id ? "…" : "REVOKE"}
                    </button>
                  </td>
                </tr>
              ))}
              {revoked.length > 0 && (
                <>
                  <tr className="bg-muted/30">
                    <td colSpan={scope === "org" ? 7 : 6} className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                      Revoked ({revoked.length})
                    </td>
                  </tr>
                  {revoked.map((r) => (
                    <tr key={r.id} className="border-t border-border opacity-50" data-testid={`session-row-revoked-${r.id}`}>
                      {scope === "org" && (
                        <td className="px-3 py-2 truncate line-through">{r.user_name || r.user_email}</td>
                      )}
                      <td className="px-3 py-2 line-through">{r.browser} on {r.os}</td>
                      <td className="px-3 py-2 text-muted-foreground">{locationOf(r) || "—"}</td>
                      <td className="px-3 py-2 mono text-xs">{r.ip_address || "—"}</td>
                      <td className="px-3 py-2 text-muted-foreground">{relativeFromNow(r.last_seen_at)}</td>
                      <td className="px-3 py-2 text-muted-foreground">{fmtDate(r.created_at)}</td>
                      <td className="px-3 py-2 text-right text-[10px] uppercase tracking-wide text-muted-foreground">
                        {relativeFromNow(r.revoked_at)}
                      </td>
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
